import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import V2Layout from '../components/V2Layout';
import useV2Auth from '../hooks/useV2Auth';
import { loadReferralSummary } from '../../containers/ReferralPage/reducer';
import {
  selectReferralLoading,
  selectReferralSummary,
} from '../../containers/ReferralPage/selectors';

const V2Referral: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('translation');
  const { user } = useV2Auth();
  const [compact, setCompact] = React.useState(false);

  const summary = useSelector(selectReferralSummary);
  const loading = useSelector(selectReferralLoading);

  React.useEffect(() => {
    if (user) {
      dispatch(loadReferralSummary());
    }
  }, [dispatch, user]);

  const handleCopy = React.useCallback(
    async (value: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        toast.success(t('referral_copied'));
      } catch {
        toast.error(t('referral_copy_failed'));
      }
    },
    [t],
  );

  return (
    <V2Layout
      activeNav="referral"
      compact={compact}
      onToggleCompact={() => setCompact((value) => !value)}
      userEmail={user?.email}
      breadcrumbs={
        <p className="breadcrumbs">
          Рефералы <span>/</span> <strong>Приглашения</strong>
        </p>
      }
      footerNote="Реферальная программа"
    >
      <section className="player-heading player-heading--page">
        <div>
          <div className="player-title-line">
            <h1>Пригласить друга</h1>
          </div>
          <p className="profile-description">Получайте процент с подписок приглашённых игроков</p>
        </div>
      </section>

      <section className="kpi-grid referral-stats" aria-label="Реферальная статистика">
        <div className="kpi">
          <p className="kpi-label">Баланс</p>
          <p className="kpi-value positive">{summary ? `$${summary.balance.toFixed(2)}` : '—'}</p>
        </div>
        <div className="kpi">
          <p className="kpi-label">Приглашено</p>
          <p className="kpi-value">
            {summary ? summary.totalReferrals : '—'}
            <small>друзей</small>
          </p>
        </div>
        <div className="kpi">
          <p className="kpi-label">Активных подписок</p>
          <p className="kpi-value">
            {summary ? summary.activeSubscriptions : '—'}
            <small>сейчас</small>
          </p>
        </div>
      </section>

      <section className="settings-panel referral-link-panel" aria-busy={loading}>
        <div className="block-heading">
          <h2>Ваша ссылка</h2>
          <span className="muted">{loading ? 'Загрузка…' : 'Поделитесь с друзьями'}</span>
        </div>
        <div className="referral-link-controls">
          <input
            className="referral-link-input"
            type="text"
            readOnly
            spellCheck={false}
            value={summary?.referralLink ?? ''}
            placeholder={loading ? t('referral_loading') : undefined}
            aria-label="Реферальная ссылка"
            onFocus={(event) => event.currentTarget.select()}
          />
          <button
            type="button"
            className="acid-button referral-copy-button"
            disabled={loading || !summary?.referralLink}
            onClick={() => handleCopy(summary?.referralLink ?? '')}
          >
            {t('referral_copy')}
          </button>
        </div>
        <p className="table-hint">
          Код: <strong>{summary?.referralCode ?? '—'}</strong>
        </p>
      </section>

      <section className="history-section referral-history">
        <div className="block-heading">
          <h2>Мои рефералы</h2>
        </div>
        {summary && summary.referrals.length > 0 ? (
          <div className="table-scroll" tabIndex={0}>
            <table className="history-table">
              <thead>
                <tr>
                  {['Email', 'Статус', 'Подписка до'].map((title) => (
                    <th key={title} scope="col">
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.referrals.map((referral) => (
                  <tr key={referral.email}>
                    <td>{referral.email}</td>
                    <td>
                      {referral.hasSubscription
                        ? 'Активна'
                        : referral.isConfirmed
                          ? 'Без подписки'
                          : 'Ожидает'}
                    </td>
                    <td>
                      {referral.subscriptionDate
                        ? dayjs(referral.subscriptionDate).format('YYYY-MM-DD')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="table-hint">
            {loading ? 'Загрузка…' : 'Пока нет приглашённых пользователей.'}
          </p>
        )}
      </section>
    </V2Layout>
  );
};

export default V2Referral;
